import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Megaphone, ChevronRight, X as XIcon } from 'lucide-react';
import api from '@/lib/api';

const DISMISS_KEY = 'valnexo_directive_banner_dismissed_at';
const DISMISS_TTL_MS = 4 * 60 * 60 * 1000;  // 4h — reviens après ce délai

/**
 * Bandeau global "Tu as X directive(s) Direction non consultée(s)".
 * Monté dans le Layout principal → visible sur toutes les pages.
 *
 * - Charge `/api/directives/nudge/pending` toutes les 5 min
 * - Sticky en haut, non bloquant
 * - Bouton X pour masquer temporairement (4h)
 * - Clic sur le bandeau → /dashboard avec ancre vers le widget
 * - S'auto-masque si on est déjà sur /dashboard (le widget est visible)
 */
export default function DirectiveGlobalBanner() {
    const [nudges, setNudges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dismissed, setDismissed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Check si un dismiss est encore valide
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const ts = parseInt(raw, 10);
                if (Number.isFinite(ts) && (Date.now() - ts) < DISMISS_TTL_MS) {
                    setDismissed(true);
                    return;
                }
                localStorage.removeItem(DISMISS_KEY);
            }
        } catch { /* noop */ }
    }, []);

    // Charge les nudges
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await api.get('/directives/nudge/pending', { timeout: 8000 });
                if (!mounted) return;
                setNudges(res.data?.nudges || []);
            } catch {
                // silencieux — bandeau disparaît si backend indispo
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        const t = setInterval(load, 5 * 60 * 1000);  // 5 min
        return () => { mounted = false; clearInterval(t); };
    }, []);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
        setDismissed(true);
    };

    const goToWidget = () => {
        navigate('/dashboard');
    };

    // Ne rien afficher : loading, aucun nudge, déjà dismissé, ou déjà sur dashboard
    if (loading || nudges.length === 0 || dismissed || location.pathname === '/dashboard') return null;

    const first = nudges[0];
    const count = nudges.length;
    const hasHighUrgency = nudges.some((n) => n.urgency === 'high');

    return (
        <div
            data-testid="directive-global-banner"
            className={`sticky top-0 z-40 border-b ${
                hasHighUrgency
                    ? 'bg-red-50 border-red-300'
                    : 'bg-purple-50 border-purple-300'
            }`}
        >
            <div className="max-w-7xl mx-auto flex items-center gap-3 px-4 py-2.5">
                <Megaphone className={`w-5 h-5 shrink-0 ${hasHighUrgency ? 'text-red-700' : 'text-purple-700'}`} />
                <button
                    type="button"
                    onClick={goToWidget}
                    className={`flex-1 text-left flex items-center gap-2 min-w-0 group ${
                        hasHighUrgency ? 'text-red-900' : 'text-purple-900'
                    }`}
                    data-testid="directive-banner-cta"
                >
                    <span className="text-sm font-semibold">
                        {count === 1 ? (
                            <>Tu as une directive Direction non consultée : <span className="underline">{first.title}</span></>
                        ) : (
                            <>Tu as {count} directives Direction non consultées</>
                        )}
                        {first.days_since_sent > 0 && (
                            <span className={`ml-2 text-xs font-normal ${hasHighUrgency ? 'text-red-700' : 'text-purple-700'}`}>
                                (depuis {first.days_since_sent}j)
                            </span>
                        )}
                    </span>
                    <ChevronRight className={`w-4 h-4 shrink-0 transition-transform group-hover:translate-x-0.5 ${
                        hasHighUrgency ? 'text-red-600' : 'text-purple-600'
                    }`} />
                </button>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Masquer temporairement"
                    className={`shrink-0 rounded-full p-1 hover:bg-white/50 ${
                        hasHighUrgency ? 'text-red-500' : 'text-purple-500'
                    }`}
                    data-testid="directive-banner-dismiss"
                >
                    <XIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
